"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import {
  FREQUENCY_TYPE,
  DAY_OF_WEEK_NAME,
  RecurringTemplateItem,
  RecurringOrder,
} from "../services/recurring-order-service";
import { OrderService } from "../../order-list/services/order-service";
import { ServiceMapService } from "../../order/services/service-map-service";
import { ServiceAddonService } from "../../order/services/service-addon-service";
import { CustomerPricingService } from "../../customer-pricing/services/customer-pricing-service";
import { isSuccess } from "../../../models/response/base-response";

/** Dịch vụ mặc định khi khách chưa có giá riêng (giống script dựng lại template đơn định kỳ). */
const DEFAULT_SERVICE_CODE = "DVGS0000";

interface CustomerOption {
  id: number;
  name: string;
  phone?: string;
  address?: string;
}

interface CreateRecurringOrderModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateFormData) => void;
  loading?: boolean;
  customers: CustomerOption[];
  branches: Array<{ id: number; name: string }>;
  services: Array<{ id: number; name: string; price?: number }>;
  addons: Array<{ id: number; name: string; price?: number }>;
  defaultCustomerId?: number;
  /** "edit" => sửa đơn định kỳ đang có; khoá ô Khách hàng + Chi nhánh */
  mode?: "create" | "edit";
  /** Dữ liệu đơn định kỳ khi mode="edit" (đã gọi detail trước đó) */
  initial?: RecurringOrder | null;
}

export interface CreateFormData {
  customer_id: number;
  branch_id: number;
  frequency_type: number;
  frequency_value: string;
  start_date: string;
  end_date?: string;
  preferred_time: string;
  template_items: RecurringTemplateItem[];
  is_pickup_laundry: number;
  requires_counting: number;
  prefill_count_from_last: number;
  note: string;
  address: string;
  payment_method: number;
  is_debit: number;
  amount_discount: number;
  workflow_type: number;
}

interface OrderSearchResult {
  id: number;
  code?: string;
  customer_name?: string;
  created_at?: string;
  total_amount?: number | string;
  items?: Array<{ item_id: number; item_type: number; quantity: number; item_name?: string }>;
}

const INITIAL_STATE = {
  customerId: 0,
  branchId: 0,
  frequencyType: FREQUENCY_TYPE.WEEKLY,
  weeklyDays: [1] as number[],
  intervalDays: 7,
  monthlyDates: [1] as number[],
  startDate: "",
  endDate: "",
  preferredTime: "08:00",
  selectedItems: [] as RecurringTemplateItem[],
  isPickup: 1,
  requiresCounting: 0,
  prefillCountFromLast: 1,
  note: "",
  address: "",
  paymentMethod: 1,
  isDebit: 0,
  amountDiscount: 0,
  customerSearch: "",
  showOrderSearch: false,
  orderSearchKey: "",
  orderResults: [] as OrderSearchResult[],
  searchingOrders: false,
  copiedFromOrder: null as string | null,
};

const RecurringOrderCreateModal: React.FC<CreateRecurringOrderModalProps> = ({
  open,
  onClose,
  onSubmit,
  loading,
  customers,
  branches,
  services,
  addons,
  defaultCustomerId,
  mode = "create",
  initial = null,
}) => {
  const isEdit = mode === "edit" && !!initial;
  // So sánh id theo Number — id khách là bigint, có thể về dạng string (trap #4)
  const findCustomer = (cid: number) => customers.find((c) => Number(c.id) === Number(cid));
  const [customerId, setCustomerId] = useState(INITIAL_STATE.customerId);
  const [branchId, setBranchId] = useState(INITIAL_STATE.branchId);
  const [frequencyType, setFrequencyType] = useState(INITIAL_STATE.frequencyType);
  const [weeklyDays, setWeeklyDays] = useState(INITIAL_STATE.weeklyDays);
  const [intervalDays, setIntervalDays] = useState(INITIAL_STATE.intervalDays);
  const [monthlyDates, setMonthlyDates] = useState(INITIAL_STATE.monthlyDates);
  const [startDate, setStartDate] = useState(INITIAL_STATE.startDate);
  const [endDate, setEndDate] = useState(INITIAL_STATE.endDate);
  const [preferredTime, setPreferredTime] = useState(INITIAL_STATE.preferredTime);
  const [selectedItems, setSelectedItems] = useState(INITIAL_STATE.selectedItems);
  // Bản mới nhất của selectedItems cho effect async (tự nạp dịch vụ của khách)
  const selectedItemsRef = useRef(selectedItems);
  selectedItemsRef.current = selectedItems;

  // Services/addons nạp theo chi nhánh của modal (giống POS), kèm tên + giá
  const [svcOptions, setSvcOptions] = useState<
    Array<{ id: number; name: string; price: number; unitName?: string; code?: string }>
  >([]);
  // Chi nhánh mà svcOptions đang thuộc về — chỉ tự điền dịch vụ của khách khi đã nạp đúng chi nhánh
  const [svcBranchId, setSvcBranchId] = useState(0);
  const [addonOptions, setAddonOptions] = useState<Array<{ id: number; name: string; price: number }>>([]);
  const { fetch: fetchSvc } = ServiceMapService.listActive();
  const { fetch: fetchAddon } = ServiceAddonService.listActive();
  const { fetch: fetchCustomerPricing } = CustomerPricingService.list();

  // Giá riêng của khách theo id dòng giá — để hiển thị giá sát thực tế (giá thật khi sinh đơn vẫn do BE tính)
  const [customPrices, setCustomPrices] = useState<Record<number, number>>({});
  const [autoFillNote, setAutoFillNote] = useState("");
  // Cặp "khách-chi nhánh" đã tự điền — tránh điền lại khi render, và bỏ qua khi vừa copy từ đơn cũ
  const autoFillKeyRef = useRef("");

  useEffect(() => {
    if (!open || !branchId) {
      setSvcOptions([]);
      setSvcBranchId(0);
      setAddonOptions([]);
      return;
    }
    let active = true;
    (async () => {
      try {
        const res = await fetchSvc({ status: 1, branch_id: branchId, limit: 1000 } as any);
        if (active && res && isSuccess(res)) {
          const d = (res as any)?.data;
          const list = Array.isArray(d) ? d : (d?.list ?? d?.data ?? []);
          setSvcOptions(
            list
              .filter((s: any) => s?.service?.id)
              .map((s: any) => ({
                id: s.id, // service_map_id
                name:
                  [s.service?.name, s.serviceType?.name || s.service_type?.name]
                    .filter(Boolean)
                    .join(" - ") || `DV #${s.id}`,
                price: Number(s.price) || 0,
                unitName: s.unit_name || "", // đơn vị bán từ service_type_maps
                code: s.serviceType?.code || s.service_type?.code || "",
              })),
          );
          setSvcBranchId(branchId);
        }
      } catch {
        if (active) setSvcOptions([]);
      }
    })();
    (async () => {
      try {
        const res = await fetchAddon({ status: 1, branch_id: branchId, limit: 1000 } as any);
        if (active && res && isSuccess(res)) {
          const d = (res as any)?.data;
          const list = Array.isArray(d) ? d : (d?.list ?? d?.data ?? []);
          setAddonOptions(
            list.map((a: any) => ({
              id: a.id,
              name: a.name || a.addon_name || `Addon #${a.id}`,
              price: Number(a.price) || 0,
            })),
          );
        }
      } catch {
        if (active) setAddonOptions([]);
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, branchId]);

  // Đã chọn chi nhánh -> CHỈ dùng list nạp theo branch (kể cả rỗng), tránh hiện nhầm
  // dịch vụ/addon của chi nhánh khác từ prop. Chưa chọn branch thì dùng prop ban đầu.
  const serviceList = branchId ? svcOptions : services;
  const addonList = branchId ? addonOptions : addons;
  const [isPickup, setIsPickup] = useState(INITIAL_STATE.isPickup);
  const [requiresCounting, setRequiresCounting] = useState(INITIAL_STATE.requiresCounting);
  const [prefillCountFromLast, setPrefillCountFromLast] = useState(INITIAL_STATE.prefillCountFromLast);
  const [note, setNote] = useState(INITIAL_STATE.note);
  const [address, setAddress] = useState(INITIAL_STATE.address);
  const [paymentMethod, setPaymentMethod] = useState(INITIAL_STATE.paymentMethod);
  const [isDebit, setIsDebit] = useState(INITIAL_STATE.isDebit);
  const [amountDiscount, setAmountDiscount] = useState(INITIAL_STATE.amountDiscount);
  const [customerSearch, setCustomerSearch] = useState(INITIAL_STATE.customerSearch);

  // Copy từ đơn cũ
  const [showOrderSearch, setShowOrderSearch] = useState(INITIAL_STATE.showOrderSearch);
  const [orderSearchKey, setOrderSearchKey] = useState(INITIAL_STATE.orderSearchKey);
  const [orderResults, setOrderResults] = useState(INITIAL_STATE.orderResults);
  const [searchingOrders, setSearchingOrders] = useState(INITIAL_STATE.searchingOrders);
  const [copiedFromOrder, setCopiedFromOrder] = useState(INITIAL_STATE.copiedFromOrder);

  const { fetch: fetchOrderList } = OrderService.list();
  const { fetch: fetchOrderDetail } = OrderService.detail();

  // Debounce timer refs
  const customerDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const orderDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset form khi mở modal
  useEffect(() => {
    if (!open) return;

    setShowOrderSearch(false);
    setOrderSearchKey("");
    setOrderResults([]);
    setSearchingOrders(false);
    setCopiedFromOrder(null);
    setCustomPrices({});
    setAutoFillNote("");
    autoFillKeyRef.current = "";

    if (isEdit && initial) {
      // ==== Prefill từ đơn định kỳ đang sửa ====
      // Khách hàng + chi nhánh KHÔNG sửa được (BE bỏ qua) — chỉ hiển thị readonly.
      setCustomerId(Number(initial.customer_id) || 0);
      setBranchId(Number(initial.branch_id) || 0);
      setFrequencyType(Number(initial.frequency_type) || FREQUENCY_TYPE.WEEKLY);
      const fv = String(initial.frequency_value ?? "");
      const nums = fv.split(",").map((x) => Number(x.trim())).filter((n) => !isNaN(n));
      if (Number(initial.frequency_type) === FREQUENCY_TYPE.WEEKLY) setWeeklyDays(nums.length ? nums : [1]);
      else setWeeklyDays([1]);
      if (Number(initial.frequency_type) === FREQUENCY_TYPE.INTERVAL_DAYS) setIntervalDays(Number(fv) || 7);
      else setIntervalDays(7);
      if (Number(initial.frequency_type) === FREQUENCY_TYPE.MONTHLY) setMonthlyDates(nums.length ? nums : [1]);
      else setMonthlyDates([1]);
      setStartDate(initial.start_date || "");
      setEndDate(initial.end_date || "");
      setPreferredTime(initial.preferred_time || "08:00");
      setSelectedItems(
        (initial.template_items || []).map((i) => ({
          item_id: Number(i.item_id),
          item_type: Number(i.item_type) || 1,
          quantity: Number(i.quantity) || 1,
        })),
      );
      setIsPickup(Number(initial.is_pickup_laundry) === 0 ? 0 : 1);
      setRequiresCounting(Number((initial as any).requires_counting) === 1 ? 1 : 0);
      setPrefillCountFromLast(Number((initial as any).prefill_count_from_last) === 1 ? 1 : 0);
      setNote(initial.note || "");
      setAddress(initial.address || "");
      setPaymentMethod(Number(initial.payment_method) || 1);
      setIsDebit(Number(initial.is_debit) || 0);
      setAmountDiscount(Number(initial.amount_discount) || 0);
      setCustomerSearch("");
      return;
    }

    // ==== Tạo mới ====
    setCustomerId(defaultCustomerId || 0);
    setBranchId(branches[0]?.id ?? 0); // mặc định chi nhánh đầu -> trigger nạp services/addons theo branch
    setFrequencyType(FREQUENCY_TYPE.WEEKLY);
    setWeeklyDays([1]);
    setIntervalDays(7);
    setMonthlyDates([1]);
    setStartDate("");
    setEndDate("");
    setPreferredTime("08:00");
    setSelectedItems([]);
    setIsPickup(1);
    setRequiresCounting(0);
    setPrefillCountFromLast(1);
    setNote("");
    setAddress("");
    setPaymentMethod(1);
    setIsDebit(0);
    setAmountDiscount(0);
    setCustomerSearch("");

    // Auto-fill address nếu có defaultCustomerId
    if (defaultCustomerId) {
      const cust = customers.find((c) => c.id === defaultCustomerId);
      if (cust?.address) setAddress(cust.address);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Auto-fill thông tin khi chọn khách hàng
  const handleSelectCustomer = useCallback((cust: CustomerOption) => {
    setCustomerId(cust.id);
    if (cust.address) setAddress(cust.address);
    // Auto-fill nhu cầu kiểm đếm mặc định từ khách hàng
    setRequiresCounting(Number((cust as any)?.requires_counting) === 1 ? 1 : 0);
  }, []);

  /**
   * Tự nạp dịch vụ của khách khi chọn khách / đổi chi nhánh (chỉ khi TẠO mới):
   * - Có giá riêng → toàn bộ dịch vụ có giá riêng mà chi nhánh đang chọn dùng được, SL = 1.
   * - Không có → 1 dòng dịch vụ mặc định (DVGS0000).
   * Danh sách đang có dòng → hỏi trước khi thay. Khi SỬA: không đổi dịch vụ, chỉ nạp giá riêng để hiển thị.
   * Chờ svcOptions nạp xong đúng chi nhánh để lọc được dịch vụ dùng được và tìm DVGS0000.
   */
  useEffect(() => {
    if (!open || !customerId || !branchId || svcBranchId !== branchId) return;
    const key = `${customerId}-${branchId}`;
    if (autoFillKeyRef.current === key) return;
    autoFillKeyRef.current = key;

    let active = true;
    (async () => {
      let pricings: any[] = [];
      try {
        const res = await fetchCustomerPricing({ customer_id: customerId, branch_id: branchId, status: 1 });
        // Kiểu trong customer-pricing-service bị bọc BaseResponse 2 lần; lúc chạy res.data là mảng giá riêng
        const raw: any = res && isSuccess(res) ? res.data : [];
        pricings = Array.isArray(raw) ? raw : raw?.list ?? [];
      } catch {
        pricings = [];
      }
      if (!active) return;

      // 1 giá / dịch vụ: giá của chi nhánh ưu tiên hơn giá áp dụng mọi chi nhánh (branch_id = 0)
      const prices: Record<number, number> = {};
      [...pricings]
        .sort((a, b) => Number(a.branch_id) - Number(b.branch_id))
        .forEach((p) => {
          prices[Number(p.service_type_map_id)] = Number(p.custom_price) || 0;
        });
      setCustomPrices(prices);
      if (isEdit) return;

      const usableIds = new Set(svcOptions.map((s) => s.id));
      let nextItems: RecurringTemplateItem[] = Object.keys(prices)
        .map(Number)
        .filter((id) => usableIds.has(id))
        .sort((a, b) => a - b)
        .map((id) => ({ item_id: id, item_type: 1, quantity: 1 }));

      let note = nextItems.length ? `Đã nạp ${nextItems.length} dịch vụ có giá riêng của khách` : "";
      if (nextItems.length === 0) {
        const fallback = svcOptions.find((s) => s.code === DEFAULT_SERVICE_CODE);
        if (fallback) {
          nextItems = [{ item_id: fallback.id, item_type: 1, quantity: 1 }];
          note = `Khách chưa có giá riêng — đã nạp dịch vụ mặc định ${DEFAULT_SERVICE_CODE}`;
        } else {
          note = `Khách chưa có giá riêng và không tìm thấy dịch vụ mặc định ${DEFAULT_SERVICE_CODE} ở chi nhánh này`;
        }
      }
      if (nextItems.length === 0) {
        setAutoFillNote(note);
        return;
      }

      // Danh sách đang có dòng (thêm tay / copy từ đơn cũ) → hỏi trước khi thay
      if (
        selectedItemsRef.current.length > 0 &&
        !window.confirm("Thay danh sách dịch vụ hiện tại bằng dịch vụ của khách?")
      ) {
        setAutoFillNote("");
        return;
      }
      setSelectedItems(nextItems);
      setAutoFillNote(note);
    })();

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, customerId, branchId, svcBranchId, isEdit]);

  // Debounce tìm đơn cũ
  const searchOrders = useCallback(async (keyword: string) => {
    if (!keyword.trim()) {
      setOrderResults([]);
      return;
    }
    setSearchingOrders(true);
    try {
      const params: any = { limit: 10, key_search: keyword.trim() };
      if (customerId) params.customer_id = customerId;
      const res = await fetchOrderList(params);
      if (res && isSuccess(res)) {
        const data = res.data;
        let list: any[] = [];
        if (Array.isArray(data)) {
          list = data;
        } else if (data && typeof data === "object") {
          list = (data as any).list ?? (data as any).items ?? [];
        }
        setOrderResults(
          list.map((o: any) => ({
            id: o.id,
            code: o.code,
            customer_name: o.customer?.name,
            created_at: o.created_at,
            total_amount: o.total_amount,
            items: o.items,
          }))
        );
      }
    } catch {
      // ignore
    } finally {
      setSearchingOrders(false);
    }
  }, [customerId, fetchOrderList]);

  // Auto-search orders khi gõ (debounce 500ms)
  const handleOrderSearchChange = (value: string) => {
    setOrderSearchKey(value);
    if (orderDebounceRef.current) clearTimeout(orderDebounceRef.current);
    orderDebounceRef.current = setTimeout(() => {
      searchOrders(value);
    }, 500);
  };

  // Copy items + thông tin từ đơn cũ
  const copyFromOrder = async (order: OrderSearchResult) => {
    try {
      const res = await fetchOrderDetail(order.id);
      if (res && isSuccess(res)) {
        const detail = res.data as any;
        const items: Array<{ item_id: number; item_type: number; quantity: number }> = detail?.items || order.items || [];
        if (items.length > 0) {
          // Người dùng đã chọn dịch vụ từ đơn cũ → không tự nạp dịch vụ của khách đè lên
          const nextCustomer = Number(detail?.customer_id) || customerId;
          const nextBranch = Number(detail?.branch_id) || branchId;
          autoFillKeyRef.current = `${nextCustomer}-${nextBranch}`;
          setAutoFillNote("");
          setSelectedItems(
            items.map((i: any) => ({
              item_id: i.item_id,
              item_type: i.item_type || 1,
              quantity: i.quantity || 1,
            }))
          );

          // Auto-fill thông tin từ đơn cũ
          if (detail?.customer_id) {
            setCustomerId(Number(detail.customer_id));
            const cust = customers.find((c) => c.id === Number(detail.customer_id));
            if (cust) {
              setCustomerSearch(cust.name || "");
              if (cust.address) setAddress(cust.address);
            }
          }
          if (detail?.branch_id) setBranchId(Number(detail.branch_id));
          if (detail?.address) setAddress(detail.address);
          if (detail?.note) setNote(detail.note);
          if (detail?.payment_method) setPaymentMethod(Number(detail.payment_method));
          if (detail?.is_debit !== undefined) setIsDebit(Number(detail.is_debit));
          if (detail?.amount_discount) setAmountDiscount(Number(detail.amount_discount));
          if (detail?.is_pickup_laundry !== undefined) setIsPickup(Number(detail.is_pickup_laundry));
          if (detail?.requires_counting !== undefined) setRequiresCounting(Number(detail.requires_counting) === 1 ? 1 : 0);
          if (detail?.prefill_count_from_last !== undefined) setPrefillCountFromLast(Number(detail.prefill_count_from_last) === 1 ? 1 : 0);

          setCopiedFromOrder(order.code || `#${order.id}`);
          setShowOrderSearch(false);
          setOrderResults([]);
          setOrderSearchKey("");
        } else {
          alert("Đơn hàng này không có dịch vụ nào");
        }
      }
    } catch {
      alert("Không thể lấy chi tiết đơn hàng");
    }
  };

  if (!open) return null;

  const filteredCustomers = customerSearch
    ? customers.filter(
        (c) =>
          c.name?.toLowerCase().includes(customerSearch.toLowerCase()) ||
          c.phone?.includes(customerSearch)
      )
    : customers.slice(0, 20);

  const getFrequencyValue = (): string => {
    switch (frequencyType) {
      case FREQUENCY_TYPE.WEEKLY:
        return weeklyDays.sort((a, b) => a - b).join(",");
      case FREQUENCY_TYPE.INTERVAL_DAYS:
        return String(intervalDays);
      case FREQUENCY_TYPE.MONTHLY:
        return monthlyDates.sort((a, b) => a - b).join(",");
      default:
        return "";
    }
  };

  /** Đơn giá hiển thị: giá riêng của khách (nếu có) cho dịch vụ, còn lại giá bảng giá. */
  const unitPriceOf = (item: RecurringTemplateItem): number => {
    if (item.item_type === 1 && customPrices[item.item_id] !== undefined) return customPrices[item.item_id];
    const src = item.item_type === 1 ? serviceList : addonList;
    return Number(src.find((x) => x.id === item.item_id)?.price) || 0;
  };

  const addServiceItem = (serviceId: number) => {
    if (selectedItems.some((i) => i.item_id === serviceId && i.item_type === 1)) return;
    setSelectedItems([...selectedItems, { item_id: serviceId, item_type: 1, quantity: 1 }]);
  };

  const addAddonItem = (addonId: number) => {
    if (selectedItems.some((i) => i.item_id === addonId && i.item_type === 2)) return;
    setSelectedItems([...selectedItems, { item_id: addonId, item_type: 2, quantity: 1 }]);
  };

  const removeItem = (index: number) => {
    setSelectedItems(selectedItems.filter((_, i) => i !== index));
  };

  const updateItemQuantity = (index: number, qty: number) => {
    const updated = [...selectedItems];
    updated[index].quantity = Math.max(1, qty);
    setSelectedItems(updated);
  };

  const handleSubmit = () => {
    if (!customerId) return alert("Vui lòng chọn khách hàng");
    if (!branchId) return alert("Vui lòng chọn chi nhánh");
    if (!startDate) return alert("Vui lòng chọn ngày bắt đầu");
    if (selectedItems.length === 0) return alert("Vui lòng chọn dịch vụ");

    onSubmit({
      customer_id: Number(customerId),
      branch_id: Number(branchId),
      frequency_type: Number(frequencyType),
      frequency_value: getFrequencyValue(),
      start_date: startDate,
      end_date: endDate || undefined,
      preferred_time: preferredTime,
      template_items: selectedItems,
      is_pickup_laundry: isPickup,
      requires_counting: requiresCounting,
      prefill_count_from_last: prefillCountFromLast,
      note,
      address,
      payment_method: paymentMethod,
      is_debit: isDebit,
      amount_discount: amountDiscount,
      workflow_type: 1,
    });
  };

  const toggleWeeklyDay = (day: number) => {
    setWeeklyDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const toggleMonthlyDate = (date: number) => {
    setMonthlyDates((prev) =>
      prev.includes(date) ? prev.filter((d) => d !== date) : [...prev, date]
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-bold">
            {isEdit
              ? `Sửa đơn định kỳ của khách ${findCustomer(customerId)?.name || `#${customerId}`}`
              : "Tạo đơn định kỳ mới"}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>

        <div className="px-6 py-4 space-y-4">
          {/* Khách hàng */}
          <div>
            <label className="block text-sm font-medium mb-1">Khách hàng *</label>
            {isEdit && (
              <p className="text-xs text-gray-400 mb-1">
                Không đổi được khách hàng / chi nhánh của đơn định kỳ đang chạy — tạo đơn mới nếu cần.
              </p>
            )}
            <input
              type="text"
              placeholder="Tìm khách hàng (tên, SĐT)..."
              className="w-full border rounded px-3 py-2 text-sm mb-1 disabled:bg-gray-100"
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
              disabled={isEdit}
              hidden={isEdit}
            />
            {customerId > 0 && (
              <div className="text-sm text-green-600 font-medium mb-1">
                Đã chọn: {findCustomer(customerId)?.name || `#${customerId}`}
                {findCustomer(customerId)?.phone && (
                  <span className="text-gray-500 ml-1">
                    - {findCustomer(customerId)?.phone}
                  </span>
                )}
              </div>
            )}
            <div className="max-h-32 overflow-y-auto border rounded" hidden={isEdit}>
              {filteredCustomers.map((c) => (
                <div
                  key={c.id}
                  onClick={() => handleSelectCustomer(c)}
                  className={`px-3 py-1.5 cursor-pointer text-sm hover:bg-blue-50 ${
                    customerId === c.id ? "bg-blue-100 font-semibold" : ""
                  }`}
                >
                  {c.name} {c.phone ? `- ${c.phone}` : ""}
                  {c.address ? <span className="text-gray-400 ml-1 text-xs">({c.address})</span> : ""}
                </div>
              ))}
            </div>
          </div>

          {/* Chi nhánh */}
          <div>
            <label className="block text-sm font-medium mb-1">Chi nhánh *</label>
            <select
              className="w-full border rounded px-3 py-2 text-sm disabled:bg-gray-100"
              value={branchId}
              disabled={isEdit}
              onChange={(e) => {
                setBranchId(Number(e.target.value));
                setSelectedItems([]); // đổi chi nhánh -> dịch vụ khác -> xoá giỏ tránh lệch
              }}
            >
              <option value={0}>-- Chọn chi nhánh --</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </div>

          {/* Tần suất */}
          <div>
            <label className="block text-sm font-medium mb-1">Tần suất *</label>
            <div className="flex gap-4 mb-2">
              {[
                { value: FREQUENCY_TYPE.WEEKLY, label: "Hàng tuần" },
                { value: FREQUENCY_TYPE.INTERVAL_DAYS, label: "Theo ngày" },
                { value: FREQUENCY_TYPE.MONTHLY, label: "Hàng tháng" },
              ].map((opt) => (
                <label key={opt.value} className="flex items-center gap-1.5 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="frequency_type"
                    checked={frequencyType === opt.value}
                    onChange={() => setFrequencyType(opt.value)}
                  />
                  {opt.label}
                </label>
              ))}
            </div>

            {frequencyType === FREQUENCY_TYPE.WEEKLY && (
              <div className="flex gap-2 flex-wrap">
                {[0, 1, 2, 3, 4, 5, 6].map((day) => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => toggleWeeklyDay(day)}
                    className={`px-3 py-1 rounded text-sm border ${
                      weeklyDays.includes(day)
                        ? "bg-blue-500 text-white border-blue-500"
                        : "bg-white text-gray-600 border-gray-300 hover:border-blue-400"
                    }`}
                  >
                    {DAY_OF_WEEK_NAME[day]}
                  </button>
                ))}
              </div>
            )}

            {frequencyType === FREQUENCY_TYPE.INTERVAL_DAYS && (
              <div className="flex items-center gap-2">
                <span className="text-sm">Mỗi</span>
                <input
                  type="number"
                  min={1}
                  className="w-20 border rounded px-2 py-1 text-sm"
                  value={intervalDays}
                  onChange={(e) => setIntervalDays(Number(e.target.value))}
                />
                <span className="text-sm">ngày</span>
              </div>
            )}

            {frequencyType === FREQUENCY_TYPE.MONTHLY && (
              <div className="flex gap-1 flex-wrap">
                {Array.from({ length: 31 }, (_, i) => i + 1).map((date) => (
                  <button
                    key={date}
                    type="button"
                    onClick={() => toggleMonthlyDate(date)}
                    className={`w-8 h-8 rounded text-xs border ${
                      monthlyDates.includes(date)
                        ? "bg-blue-500 text-white border-blue-500"
                        : "bg-white text-gray-600 border-gray-300 hover:border-blue-400"
                    }`}
                  >
                    {date}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Dịch vụ */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium">Dịch vụ *</label>
              <button
                type="button"
                onClick={() => setShowOrderSearch(!showOrderSearch)}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium px-2 py-1 rounded border border-blue-200 hover:bg-blue-50 transition-colors"
              >
                {showOrderSearch ? "Ẩn" : "Copy từ đơn cũ"}
              </button>
            </div>

            {/* Copy từ đơn cũ */}
            {showOrderSearch && (
              <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="text-xs text-blue-700 font-medium mb-2">Tìm đơn hàng cũ để copy dịch vụ</div>
                <input
                  type="text"
                  placeholder="Nhập mã đơn hoặc tên khách hàng..."
                  className="w-full border rounded px-3 py-1.5 text-sm mb-2"
                  value={orderSearchKey}
                  onChange={(e) => handleOrderSearchChange(e.target.value)}
                />

                {searchingOrders && (
                  <div className="text-xs text-gray-400 text-center py-2">Đang tìm...</div>
                )}

                {orderResults.length > 0 && !searchingOrders && (
                  <div className="max-h-40 overflow-y-auto border rounded bg-white">
                    {orderResults.map((order) => (
                      <div
                        key={order.id}
                        onClick={() => copyFromOrder(order)}
                        className="px-3 py-2 cursor-pointer text-sm hover:bg-blue-50 border-b last:border-b-0 flex items-center justify-between"
                      >
                        <div>
                          <span className="font-semibold text-blue-600">{order.code || `#${order.id}`}</span>
                          {order.customer_name && (
                            <span className="text-gray-500 ml-2">- {order.customer_name}</span>
                          )}
                        </div>
                        <div className="text-xs text-gray-400">
                          {order.created_at?.split("T")[0] || ""}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {orderResults.length === 0 && orderSearchKey && !searchingOrders && (
                  <div className="text-xs text-gray-400 text-center py-2">Không tìm thấy đơn hàng</div>
                )}
              </div>
            )}

            {copiedFromOrder && (
              <div className="text-xs text-green-600 font-medium mb-2">
                Đã copy dịch vụ từ đơn {copiedFromOrder}
              </div>
            )}

            {autoFillNote && !copiedFromOrder && (
              <div className="text-xs text-blue-600 font-medium mb-2">{autoFillNote}</div>
            )}

            <div className="flex gap-2 mb-2">
              <select
                className="flex-1 border rounded px-3 py-2 text-sm"
                onChange={(e) => {
                  if (e.target.value) addServiceItem(Number(e.target.value));
                  e.target.value = "";
                }}
                defaultValue=""
              >
                <option value="">+ Thêm dịch vụ</option>
                {serviceList.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}{(s as any).unitName ? ` (${(s as any).unitName})` : ""}{s.price ? ` — ${Number(s.price).toLocaleString("vi-VN")}đ` : ""}
                  </option>
                ))}
              </select>
              <select
                className="flex-1 border rounded px-3 py-2 text-sm"
                onChange={(e) => {
                  if (e.target.value) addAddonItem(Number(e.target.value));
                  e.target.value = "";
                }}
                defaultValue=""
              >
                <option value="">+ Thêm addon</option>
                {addonList.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}{a.price ? ` — ${Number(a.price).toLocaleString("vi-VN")}đ` : ""}
                  </option>
                ))}
              </select>
            </div>

            {selectedItems.length > 0 && (
              <table className="w-full border text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="border px-2 py-1 text-left">Loại</th>
                    <th className="border px-2 py-1 text-left">Tên</th>
                    <th className="border px-2 py-1 text-right">Giá</th>
                    <th className="border px-2 py-1 text-center">SL</th>
                    <th className="border px-2 py-1 text-right">Thành tiền</th>
                    <th className="border px-2 py-1 text-center w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {selectedItems.map((item, idx) => {
                    const src = item.item_type === 1 ? serviceList : addonList;
                    const found = src.find((x) => x.id === item.item_id);
                    const name = found?.name || (item.item_type === 1 ? `DV #${item.item_id}` : `Addon #${item.item_id}`);
                    const unitName = (found as any)?.unitName || "";
                    const basePrice = Number(found?.price) || 0;
                    const price = unitPriceOf(item);
                    const lineTotal = price * item.quantity;
                    return (
                      <tr key={idx}>
                        <td className="border px-2 py-1">{item.item_type === 1 ? "Dịch vụ" : "Addon"}</td>
                        <td className="border px-2 py-1">
                          {name}
                          {unitName && (
                            <span className="block text-xs text-blue-600">ĐVT: {unitName}</span>
                          )}
                        </td>
                        <td className="border px-2 py-1 text-right">
                          {price.toLocaleString("vi-VN")}đ
                          {price !== basePrice && (
                            <span className="block text-xs text-gray-400 line-through" title="Giá gốc bảng giá">
                              {basePrice.toLocaleString("vi-VN")}đ
                            </span>
                          )}
                        </td>
                        <td className="border px-2 py-1 text-center">
                          <input
                            type="number"
                            min={1}
                            className="w-16 border rounded px-1 py-0.5 text-center text-sm"
                            value={item.quantity}
                            onChange={(e) => updateItemQuantity(idx, Number(e.target.value))}
                          />
                        </td>
                        <td className="border px-2 py-1 text-right">{lineTotal.toLocaleString("vi-VN")}đ</td>
                        <td className="border px-2 py-1 text-center">
                          <button
                            type="button"
                            onClick={() => removeItem(idx)}
                            className="text-red-500 hover:text-red-700"
                          >
                            &times;
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50 font-medium">
                    <td className="border px-2 py-1 text-right" colSpan={4}>Tổng tạm tính</td>
                    <td className="border px-2 py-1 text-right" colSpan={2}>
                      {selectedItems
                        .reduce((sum, item) => sum + unitPriceOf(item) * item.quantity, 0)
                        .toLocaleString("vi-VN")}đ
                    </td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>

          {/* Thời gian */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Ngày bắt đầu *</label>
              <input
                type="date"
                className="w-full border rounded px-3 py-2 text-sm"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Ngày kết thúc</label>
              <input
                type="date"
                className="w-full border rounded px-3 py-2 text-sm"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Giờ tạo đơn</label>
              <input
                type="time"
                className="w-full border rounded px-3 py-2 text-sm"
                value={preferredTime}
                onChange={(e) => setPreferredTime(e.target.value)}
              />
            </div>
          </div>

          {/* Cài đặt */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Địa chỉ</label>
              <input
                type="text"
                className="w-full border rounded px-3 py-2 text-sm"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Địa chỉ lấy/giao"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Ghi chú</label>
              <input
                type="text"
                className="w-full border rounded px-3 py-2 text-sm"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Ghi chú đơn hàng"
              />
            </div>
          </div>

          <div className="grid grid-cols-4 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Thanh toán</label>
              <select
                className="w-full border rounded px-3 py-2 text-sm"
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(Number(e.target.value))}
              >
                <option value={1}>Tiền mặt</option>
                <option value={2}>QR Code</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Chiết khấu (VND)</label>
              <input
                type="number"
                min={0}
                className="w-full border rounded px-3 py-2 text-sm"
                value={amountDiscount}
                onChange={(e) => setAmountDiscount(Math.max(0, Number(e.target.value)))}
                placeholder="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Công nợ</label>
              <select
                className="w-full border rounded px-3 py-2 text-sm"
                value={isDebit}
                onChange={(e) => setIsDebit(Number(e.target.value))}
              >
                <option value={0}>Không</option>
                <option value={1}>Có</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Lấy hàng</label>
              <select
                className="w-full border rounded px-3 py-2 text-sm"
                value={isPickup}
                onChange={(e) => setIsPickup(Number(e.target.value))}
              >
                <option value={1}>Cần lấy hàng</option>
                <option value={0}>Không cần</option>
              </select>
            </div>
          </div>

          {/* === Kiểm đếm khi nhận (với khách) === */}
          {/* Đã bỏ checkbox "lấy SL từ đơn gần nhất" — BE hard default LUÔN bật. */}
          <div className="mt-3 flex flex-col gap-2">
            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                className="h-4 w-4 text-green-600"
                checked={requiresCounting === 1}
                onChange={(e) => setRequiresCounting(e.target.checked ? 1 : 0)}
              />
              <span className="text-sm text-gray-700">Yêu cầu kiểm đếm khi nhận (với khách)</span>
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border rounded hover:bg-gray-50"
          >
            Huỷ
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? "Đang lưu..." : isEdit ? "Lưu thay đổi" : "Tạo đơn định kỳ"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RecurringOrderCreateModal;
